import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Archive, ChevronUp, Plus } from 'lucide-react'
import { useConversations } from '../../contexts/ConversationContext'
import type { Conversation } from '../../contexts/ConversationContext'
import { ConversationActionsSheet } from './ConversationActionsSheet'
// s60 — OnboardingProgressCard removido (ver nota em ChatPage.tsx)

interface ConvRowShape {
  id: string
  title: string | null
  is_archived: boolean
}

/** Long-press threshold for the row-actions sheet. */
const LONG_PRESS_MS = 450

function useLongPress(onTrigger: (conv: ConvRowShape) => void) {
  const timerRef = useRef<number | null>(null)
  const startRef = useRef<{ x: number; y: number } | null>(null)
  const firedRef = useRef(false)

  function clear() {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
    startRef.current = null
  }

  return {
    bind: (conv: ConvRowShape) => ({
      handlers: {
        onTouchStart: (e: React.TouchEvent) => {
          firedRef.current = false
          const t = e.touches[0]
          startRef.current = { x: t.clientX, y: t.clientY }
          timerRef.current = window.setTimeout(() => {
            firedRef.current = true
            if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
              navigator.vibrate?.(10)
            }
            onTrigger(conv)
          }, LONG_PRESS_MS)
        },
        onTouchMove: (e: React.TouchEvent) => {
          if (!startRef.current) return
          const t = e.touches[0]
          const dx = t.clientX - startRef.current.x
          const dy = t.clientY - startRef.current.y
          if (Math.abs(dx) > 6 || Math.abs(dy) > 6) clear()
        },
        onTouchEnd: () => clear(),
        onTouchCancel: () => clear(),
      },
      consumedByLongPress: () => firedRef.current,
    }),
  }
}

/** WhatsApp-style short time label (HH:MM / ontem / DD mmm). */
function shortTimeLabel(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  if (sameDay) {
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
  }
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  if (d.toDateString() === yesterday.toDateString()) return 'ontem'
  const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
  return `${d.getDate()} ${months[d.getMonth()]}`
}

/**
 * Row used in both sections. Taller than the desktop version (48-52px) for
 * comfortable thumb targets, but the same visual vocabulary: dot-indicator,
 * name + timestamp on the first line, preview on the second.
 */
function MobileRow({
  conv,
  unread,
  streaming,
  onPress,
  longPressHandlers,
}: {
  conv: Conversation
  unread: boolean
  streaming: boolean
  onPress: () => void
  longPressHandlers: React.HTMLAttributes<HTMLButtonElement>
}) {
  return (
    <button
      onClick={onPress}
      {...longPressHandlers}
      className="w-full text-left flex items-start gap-2.5 px-4 py-3"
      style={{
        borderBottom: '1px solid rgba(0,0,0,0.04)',
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{
          marginTop: '7px',
          background: streaming || unread ? 'var(--accent)' : 'transparent',
          animation: streaming ? 'stream-pulse 1.1s ease-in-out infinite' : undefined,
        }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span
            className="flex-1 min-w-0 truncate text-[14px]"
            style={{ fontWeight: unread ? 600 : 500, color: 'var(--ink)' }}
          >
            {conv.title || 'Nova conversa'}
          </span>
          <span
            className="shrink-0 text-[11px] tabular-nums"
            style={{
              color: unread ? 'var(--accent)' : 'var(--ink-muted)',
              fontWeight: unread ? 600 : 400,
            }}
          >
            {shortTimeLabel(conv.last_message_at || conv.created_at)}
          </span>
        </div>
        {conv.last_message_snippet && (
          <div
            className="text-[12.5px] truncate leading-snug mt-0.5"
            style={{ color: unread ? 'var(--ink-light)' : 'var(--ink-muted)' }}
          >
            {conv.last_message_snippet}
          </div>
        )}
      </div>
    </button>
  )
}

function SectionHeader({ label, count, accent = false }: { label: string; count?: number; accent?: boolean }) {
  return (
    <div
      className="flex items-center justify-between px-5 pt-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.08em]"
      style={{ color: accent ? 'var(--accent)' : 'var(--ink-muted)' }}
    >
      <span>{label}</span>
      {typeof count === 'number' && count > 0 && (
        <span className="text-[11px] font-normal normal-case tracking-normal" style={{ color: 'var(--ink-muted)' }}>
          {count}
        </span>
      )}
    </div>
  )
}

/**
 * Mobile landing at `/chat` — now mirrors the desktop sidebar:
 * - Rotinas (cream-dark background, accent header) grouped by agent
 * - Chats (free conversations + manual runs go into Rotinas as of 2026-04-20,
 *   so this section is only for agent_id IS NULL)
 * - Archive pinned at the bottom
 */
export function MobileConversationsList() {
  const navigate = useNavigate()
  const {
    rotinaConversations,
    chatConversations,
    archivedConversations,
    streamStates,
    setActiveConvId,
    markAgentConvSeen,
    loadArchived,
  } = useConversations()
  const [showArchive, setShowArchive] = useState(false)
  const [actionsFor, setActionsFor] = useState<ConvRowShape | null>(null)
  const longPress = useLongPress((conv) => setActionsFor(conv))
  const location = useLocation()

  useEffect(() => {
    setShowArchive(false)
  }, [location.pathname])

  async function toggleArchive() {
    if (!showArchive) await loadArchived()
    setShowArchive((v) => !v)
  }

  function open(conv: Conversation) {
    // Rotinas (any agent conv) get marked seen on tap — same rule as desktop.
    if (conv.agent_id) markAgentConvSeen(conv.id)
    setActiveConvId(conv.id)
    navigate(`/chat/${conv.id}`)
  }

  function newConv() {
    setActiveConvId(null)
    navigate('/chat?new=true')
  }

  const unreadRotinasCount = rotinaConversations.filter(c => !c.seen_at).length

  return (
    <div
      className="flex flex-col flex-1 overflow-hidden"
      style={{ background: 'var(--cream)', color: 'var(--ink)' }}
    >
      <header
        className="flex items-center justify-between px-5 pt-5 pb-3"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <h1
          className="m-0"
          style={{
            fontFamily: 'var(--serif)',
            fontSize: '22px',
            fontWeight: 600,
            letterSpacing: '-0.02em',
          }}
        >
          se<span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>Guru</span>
        </h1>
        <button
          onClick={newConv}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors"
          style={{
            background: 'var(--ink)',
            color: 'var(--cream)',
            border: '1px solid var(--ink)',
          }}
          aria-label="Nova conversa"
        >
          <Plus size={13} strokeWidth={2} />
          Novo
        </button>
      </header>

      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* s60 — OnboardingProgressCard removido (rotina Onboarding
            vive agora na conv dedicada em vez do card flutuante). */}

        {/* Rotinas — any conversation attached to an agent */}
        {rotinaConversations.length > 0 && (
          <section
            style={{
              background: 'var(--cream-dark)',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <SectionHeader
              label="Rotinas"
              accent
              count={unreadRotinasCount > 0 ? unreadRotinasCount : undefined}
            />
            <div>
              {rotinaConversations.map((conv) => {
                const lp = longPress.bind({
                  id: conv.id,
                  title: conv.title,
                  is_archived: conv.is_archived,
                })
                return (
                  <MobileRow
                    key={conv.id}
                    conv={conv}
                    unread={!conv.seen_at}
                    streaming={!!streamStates.get(conv.id)?.isStreaming}
                    onPress={() => {
                      if (lp.consumedByLongPress()) return
                      open(conv)
                    }}
                    longPressHandlers={lp.handlers}
                  />
                )
              })}
            </div>
          </section>
        )}

        {/* Chats — free conversations only */}
        <SectionHeader label="Chats" count={chatConversations.length} />

        {chatConversations.length === 0 && rotinaConversations.length === 0 ? (
          <p
            className="px-4 py-8 text-center text-sm"
            style={{ color: 'var(--ink-muted)' }}
          >
            Sem conversas ainda. Toca em <strong>Novo</strong> para começar.
          </p>
        ) : (
          <div>
            {chatConversations.map((conv) => {
              const lp = longPress.bind({
                id: conv.id,
                title: conv.title,
                is_archived: conv.is_archived,
              })
              return (
                <MobileRow
                  key={conv.id}
                  conv={conv}
                  unread={false}
                  streaming={!!streamStates.get(conv.id)?.isStreaming}
                  onPress={() => {
                    if (lp.consumedByLongPress()) return
                    open(conv)
                  }}
                  longPressHandlers={lp.handlers}
                />
              )
            })}
          </div>
        )}

        {/* Archive pinned at the bottom, expands upward */}
        <section
          className="mt-auto flex flex-col"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          {showArchive && (
            <div className="max-h-[50vh] overflow-y-auto">
              {archivedConversations.length === 0 ? (
                <p
                  className="px-5 py-3 text-xs"
                  style={{ color: 'var(--ink-muted)' }}
                >
                  Sem conversas arquivadas
                </p>
              ) : (
                archivedConversations.map((conv) => {
                  const lp = longPress.bind({
                    id: conv.id,
                    title: conv.title,
                    is_archived: conv.is_archived,
                  })
                  return (
                    <MobileRow
                      key={conv.id}
                      conv={conv}
                      unread={false}
                      streaming={false}
                      onPress={() => {
                        if (lp.consumedByLongPress()) return
                        open(conv)
                      }}
                      longPressHandlers={lp.handlers}
                    />
                  )
                })
              )}
            </div>
          )}
          <button
            onClick={toggleArchive}
            className="w-full flex items-center gap-2 px-5 py-3 text-xs"
            style={{
              color: 'var(--ink-muted)',
              borderTop: showArchive ? '1px solid var(--border)' : 'none',
            }}
            aria-expanded={showArchive}
          >
            <Archive size={13} strokeWidth={1.5} />
            <span>Arquivo</span>
            <ChevronUp
              size={13}
              className={`ml-auto transition-transform ${showArchive ? 'rotate-180' : ''}`}
            />
          </button>
        </section>
      </div>

      <ConversationActionsSheet
        conv={actionsFor}
        onClose={() => setActionsFor(null)}
      />
    </div>
  )
}
